import { useState, type FormEvent } from 'react';
import './Contact.css';

interface ContactProps {
  title?: string;
  subline?: string;
  buttonText?: string;
  titleAlignment?: 'left' | 'center' | 'right';
}

export default function Contact({ 
  title = "Let's bring them back to life!",
  subline = "Fill out the form below to book your free consultation.",
  buttonText = "Book Your Free Consultation",
  titleAlignment = 'left'
}: ContactProps = {}) {
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    message: '',
  });
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setSubmitting(true);

    // Netlify Forms submission
    const formDataToSubmit = new FormData();
    formDataToSubmit.append('form-name', 'contact'); // Required for Netlify Forms
    Object.keys(formData).forEach(key => {
      formDataToSubmit.append(key, formData[key as keyof typeof formData]);
    });

    try {
      const response = await fetch('/', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(formDataToSubmit as any).toString(),
      });

      if (response.ok) {
        setSubmitted(true);
        setFormData({
          firstName: '',
          lastName: '',
          email: '',
          phone: '',
          message: '',
        });
      } else {
        alert('There was an error submitting your form. Please try again.');
      }
    } catch (error) {
      alert('There was an error submitting your form. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <section id="contact" className="contact section">
        <div className="contact-container">
          <div className="contact-promo">
            <div className="contact-success-content">
              <h2>Thank You!</h2>
              <p>We've received your enquiry and will get back to you soon.</p>
            </div>
          </div>
          <div className="contact-form-wrapper">
            <div className="contact-success">
              <h2>Thank You!</h2>
              <p>We've received your enquiry and will get back to you soon.</p>
            </div>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section id="contact" className="contact section">
      <div className="contact-container">
        {/* Left Column - Promotional Section */}
        <div className="contact-promo">
          <div className="promo-content">
            <h1 className="promo-headline">
              <span className="promo-line">
                <span className="promo-itallic">Your Precious Moments</span>
              </span>
              <span className="promo-line promo-highlight">Deserve Better</span>
              <span className="promo-line promo-highlight">Than a Hard Drive</span>
            </h1>
            <p className="promo-subtitle">
              Those videos sitting on your computer? They're not just files. They're giggles and
              tantrums and tiny hands reaching for yours. They're the proof that you were there,
              that you loved them, that these moments mattered.
              <br />
              <br />
              They deserve to be seen →
            </p>
          </div>
        </div>

        {/* Right Column - Contact Form */}
        <div className="contact-form-wrapper">
          <div className="contact-form-header">
            <h2 style={{ textAlign: titleAlignment }}>{title}</h2>
            <p className="contact-form-subtitle">
              {subline}
            </p>
          </div>
          <form
            name="contact"
            method="POST"
            data-netlify="true"
            onSubmit={handleSubmit}
            className="contact-form"
          >
            <input type="hidden" name="form-name" value="contact" />

            <div className="form-row">
              <div className="form-group form-group-half">
                <label htmlFor="firstName">First Name *</label>
                <input
                  type="text"
                  id="firstName"
                  name="firstName"
                  value={formData.firstName}
                  onChange={handleChange}
                  required
                  placeholder="John"
                />
              </div>
              <div className="form-group form-group-half">
                <label htmlFor="lastName">Last Name *</label>
                <input
                  type="text"
                  id="lastName"
                  name="lastName"
                  value={formData.lastName}
                  onChange={handleChange}
                  required
                  placeholder="Doe"
                />
              </div>
            </div>

            <div className="form-group">
              <label htmlFor="email">Email *</label>
              <input
                type="email"
                id="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                required
                placeholder="your.email@example.com"
              />
            </div>

            <div className="form-group">
              <label htmlFor="phone">Phone Number *</label>
              <input
                type="tel"
                id="phone"
                name="phone"
                value={formData.phone}
                onChange={handleChange}
                required
                placeholder="+61 400 000 000"
              />
            </div>

            <div className="form-group">
              <label htmlFor="message">Message</label>
              <textarea
                id="message"
                name="message"
                value={formData.message}
                onChange={handleChange}
                placeholder="Tell us about your project..."
                rows={5}
              />
            </div>

            <button type="submit" className="submit-button" disabled={submitting}>
              {submitting ? 'Sending...' : buttonText}
            </button>
          </form>
          <p className="contact-form-footer">
            Professional editing. Personal service. Timeless results.
          </p>
        </div>
      </div>
    </section>
  );
}
